/**
 * WalletConnect — Phantom + MetaMask connect button for TopBar.
 */

import { memo, useState, useRef, useEffect } from 'react';
import { useWallet } from '../../hooks/useWallet.js';

export const WalletConnect = memo(function WalletConnect() {
  const {
    wallets, activeWallet, phantomWallet, metaMaskWallet,
    isPhantomAvailable, isMetaMaskAvailable,
    connectPhantom, connectMetaMask,
    disconnectPhantom, disconnectMetaMask,
    shortenAddress,
  } = useWallet();

  const [open, setOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const connected = wallets.length > 0;

  return (
    <div ref={dropRef} className="relative">
      <button
        onClick={() => setOpen(p => !p)}
        aria-label={connected ? 'Wallet connections' : 'Connect wallet'}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-1.5 font-mono text-[11px] px-2 py-1 border tracking-widest transition-all"
        style={{
          borderColor: connected ? 'var(--color-buy)' : 'var(--color-border)',
          color:       connected ? 'var(--color-buy)' : 'var(--color-bright)',
        }}
        onMouseEnter={e => {
          if (!connected) {
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-arc)';
            (e.currentTarget as HTMLElement).style.color = 'var(--color-arc)';
          }
        }}
        onMouseLeave={e => {
          if (!connected) {
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border)';
            (e.currentTarget as HTMLElement).style.color = 'var(--color-bright)';
          }
        }}
      >
        {connected ? (
          <>
            <span className="w-1.5 h-1.5 rounded-full bg-buy animate-blink" />
            {activeWallet ? shortenAddress(activeWallet.address) : 'WALLET'}
            <span className="opacity-50">{open ? '▲' : '▼'}</span>
          </>
        ) : (
          <>
            <span className="text-[11px]">⬡</span>
            CONNECT
            <span className="opacity-50">{open ? '▲' : '▼'}</span>
          </>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 border py-1.5 min-w-[200px] animate-slide-in"
          style={{
            background:  'var(--color-panel)',
            borderColor: 'var(--color-border)',
            boxShadow:   '0 8px 32px rgba(0,0,0,0.7)',
          }}
        >
          {/* Header */}
          <div className="px-3 pb-1.5 mb-1 border-b border-hud">
            <span className="font-mono text-[11px] tracking-widest opacity-90">WALLET CONNECTIONS</span>
          </div>

          {/* Phantom */}
          <div className="px-3 py-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[14px]">👻</span>
                <div>
                  <div className="font-mono text-[11px] tracking-wider" style={{ color: 'var(--color-bright)' }}>
                    Phantom
                  </div>
                  {phantomWallet ? (
                    <div className="font-mono text-[11px]" style={{ color: 'var(--color-buy)' }}>
                      {shortenAddress(phantomWallet.address)}
                      {phantomWallet.balance !== undefined && (
                        <span className="opacity-60 ml-1">({phantomWallet.balance.toFixed(3)} SOL)</span>
                      )}
                    </div>
                  ) : (
                    <div className="font-mono text-[11px] opacity-90">
                      {isPhantomAvailable ? 'Not connected' : 'Not installed'}
                    </div>
                  )}
                </div>
              </div>
              {phantomWallet ? (
                <button
                  onClick={() => { disconnectPhantom(); }}
                  className="font-mono text-[11px] text-sell hover:opacity-80 transition-opacity"
                >
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={() => { connectPhantom(); setOpen(false); }}
                  className="font-mono text-[11px] border border-arc/40 px-2 py-0.5 hover:bg-arc/10 transition-colors"
                  style={{ color: 'var(--color-arc)' }}
                >
                  {isPhantomAvailable ? 'Connect' : 'Install'}
                </button>
              )}
            </div>
          </div>

          <div className="border-t border-hud/50 mx-3 my-1" />

          {/* MetaMask */}
          <div className="px-3 py-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[14px]">🦊</span>
                <div>
                  <div className="font-mono text-[11px] tracking-wider" style={{ color: 'var(--color-bright)' }}>
                    MetaMask
                  </div>
                  {metaMaskWallet ? (
                    <div className="font-mono text-[11px]" style={{ color: 'var(--color-buy)' }}>
                      {shortenAddress(metaMaskWallet.address)}
                      {metaMaskWallet.balance !== undefined && (
                        <span className="opacity-60 ml-1">({metaMaskWallet.balance.toFixed(4)} ETH)</span>
                      )}
                    </div>
                  ) : (
                    <div className="font-mono text-[11px] opacity-90">
                      {isMetaMaskAvailable ? 'Not connected' : 'Not installed'}
                    </div>
                  )}
                </div>
              </div>
              {metaMaskWallet ? (
                <button
                  onClick={() => { disconnectMetaMask(); }}
                  className="font-mono text-[11px] text-sell hover:opacity-80 transition-opacity"
                >
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={() => { connectMetaMask(); setOpen(false); }}
                  className="font-mono text-[11px] border border-arc/40 px-2 py-0.5 hover:bg-arc/10 transition-colors"
                  style={{ color: 'var(--color-arc)' }}
                >
                  {isMetaMaskAvailable ? 'Connect' : 'Install'}
                </button>
              )}
            </div>
          </div>

          {/* Paper mode note */}
          <div className="px-3 pt-1.5 mt-1 border-t border-hud/50">
            <span className="font-mono text-[11px] opacity-90 leading-tight">
              Live trading requires wallet connection. Paper trading works without a wallet.
            </span>
          </div>
        </div>
      )}
    </div>
  );
});
